import React from 'react';
import ReactDOM from 'react-dom';
import { connect, Provider } from 'react-redux';
import Helmet from 'react-helmet';
import canUseDOM from 'lib/util/canUseDOM';
import { fetchClient } from 'common/actions/clientActions';
import {
    fetchExternalConfig,
    fetchEmbeddedOptions,
    analyticsReady,
    contentUnSelect,
    mediaQuery
} from 'common/actions/appActions';
import {
    createSmartAdobePassInitializeAction,
    createSmartIpAuthenticationAction
} from 'common/actions/authenticationActions';
import { loadScoringData } from 'common/actions/scoringActions';
import { intlShape } from 'common/injectIntl';
import { clearPage, scorePageBuckets } from 'page/actions';
import { createClearPlayingContentAction, createResetPlayerAction } from 'player/actions';
import { scoreScheduleModule } from 'scheduleModule/actions';
import connectAdInitializer from 'ads/connectAdInitializer';
import appSelector from 'selectors/appSelector';
import playerSelector from 'selectors/playerSelector';
import routingSelector from 'selectors/routingSelector';
import BrandingContainer from 'branding/BrandingContainer';
import AuthenticationContainer from 'modal/authn/AuthenticationContainer';
import MessagingContainer from 'modal/messaging/MessagingContainer';
import { subscribeToStore } from 'lib/observe';
import scoringObserver from 'lib/observe/scoringObserver';
import trackingObserver from 'lib/observe/trackingObserver';
import appConfig from '../../config';
import { nrCookie } from '../constants';
import IntlProvider from '../IntlProvider';

const {
    shellVersion,
    cdnHost,
    playerVersion,
    devStyles
} = appConfig;

let STYLE_HREF = `${cdnHost}/redesign/${shellVersion}/css`;
if (devStyles) {
    STYLE_HREF = 'http://localhost:8080/static/css';
}

// sanitize anchor tags in nav (don't ajax, and remove useless href)
function sanitizeAnchor(anchor) {
    if (anchor instanceof HTMLAnchorElement) {
        if (anchor.getAttribute('href') === '#') {
            anchor.removeAttribute('href');
        }
        anchor.setAttribute('data-route', 'false');
    }
}

function updateSubNav(location) {
    // updateSubNav is called only on the client side.
    if (location && window.jQuery) {
        window.jQuery('#global-nav-secondary li').each(function () { // eslint-disable-line
            const $this = window.jQuery(this);
            const href = $this.find('a[itemprop="url"]').attr('href');

            if (
                `${location.basename}${location.pathname}` === href ||
                `${location.basename}${location.pathname}/` === href
            ) {
                $this.addClass('active');
            } else if ($this.hasClass('active')) {
                $this.removeClass('active');
            }
        });
    }
}

function onLoadingStart() {
    document.body.classList.add('watch-loading');
}

function onLoadingComplete() {
    document.body.classList.remove('watch-loading');
}

function onFetchComponentData(dispatch) {
    dispatch(scorePageBuckets()).then(() => {
        dispatch(scoreScheduleModule()).then(onLoadingComplete);
    });
}

class PageShell extends React.Component {
    constructor(props) {
        super(props);
        this.mountBrandingComponent = this.mountBrandingComponent.bind(this);
        this.insertProviderContainer = this.insertProviderContainer.bind(this);
        this.providerContainer = null;
        this.subNavEl = null;
        this.latestLocation = null;
    }

    componentWillMount() {
        if (canUseDOM) {
            // this disables the shell from rendering ads (so that we can do it)
            // see https://code.espn.com/webdev/score/blob/ec82c07f8a607971029786f21fffbb211996e370/static/js/ads/init.js#L358
            if (window.espn_ui) {
                window.espn_ui.pageShell = true;
            }

            const { store, router } = this.context;
            const { dispatch } = this.props;

            this.unsubscribeFromStore = subscribeToStore(store, [
                scoringObserver,
                trackingObserver
            ]);

            const unlistenBefore = router.listenBefore((newLocation) => {
                const { player, routing } = this.props;
                if (player.embedding === true) {
                    return false;
                }

                // if the pathname is different, clear the redux state
                const { locationBeforeTransitions: currentLocation = {} } = routing;
                const currentPath = `${currentLocation.basename}${currentLocation.pathname}`;
                const newPath = `${newLocation.basename}${newLocation.pathname}`;
                const currentPathRegex = new RegExp(`^${currentPath}\/?$`);

                if (!newPath.match(currentPathRegex)) {
                    dispatch(contentUnSelect());
                    dispatch(createClearPlayingContentAction());
                    dispatch(createResetPlayerAction());
                    dispatch(clearPage());
                }
                return true;
            });

            const unlisten = router.listen((location) => {
                this.latestLocation = location;

                // this fixes broken scrolling when using the back and forward browser buttons btw ajax pages
                if (window.espn_ui && window.espn_ui.Helpers) {
                    window.espn_ui.Helpers.history.updateState('react', true);
                }

                updateSubNav(location);
            });

            this.unlisten = () => {
                unlistenBefore();
                unlisten();
            };

            dispatch(createSmartAdobePassInitializeAction());
            dispatch(createSmartIpAuthenticationAction());
            dispatch(fetchEmbeddedOptions());

            dispatch(scorePageBuckets()).then(() => {
                dispatch(scoreScheduleModule()).then(() => {
                    onLoadingComplete();
                }, () => {
                    onLoadingComplete();
                });
            }, () => {
                onLoadingComplete();
            });
        }
    }

    componentDidMount() {
        const { dispatch } = this.props;
        if (window.jQuery) {
            if (window.jQuery.subscribe) {
                window.jQuery.subscribe('userBuilt', () => {
                    setTimeout(() => {
                        const profileAnchorTags = document.querySelectorAll('.user a');
                        Array.prototype.forEach.call(profileAnchorTags, (anchor) => {
                            sanitizeAnchor(anchor);
                        });
                    }, 0);
                });
                window.jQuery.subscribe('analytics.loaded', (initData) => {
                    dispatch(analyticsReady(initData));
                });
            }

            window.jQuery(window).on('media_query_updated', () => {
                if (window.espn_ui && window.espn_ui.Helpers) {
                    const nextMq = window.espn_ui.Helpers.get_media_query_in_use();
                    dispatch(mediaQuery(nextMq));
                }
            });

            // NOTE: this may not fire locally on all browsers
            // 'nav.ready' will be fired not only the initial load but also every break point change
            window.jQuery(document).on('nav.ready', () => {
                window.jQuery('#global-nav > ul > li.watch').addClass('active');
                const headerAnchorTags = document.querySelectorAll('#header-wrapper a');
                Array.prototype.forEach.call(headerAnchorTags, (anchor) => {
                    sanitizeAnchor(anchor);
                });
                updateSubNav(this.latestLocation);
                this.mountBrandingComponent(); // in the event the nav completes after auth
            });
        }

        if (window.espn && window.espn.refresh) {
            window.espn.refresh.stop();
        }
    }

    componentWillReceiveProps(nextProps) {
        const { location = {} } = nextProps;
        const currentLocation = this.props.location;

        if (currentLocation !== location) {
            // add playback specific class to body on playback page
            if (location.pathname.indexOf('/player') !== -1) {
                if (document.body.className.indexOf('playback') === -1) {
                    document.body.className = `${document.body.className} watch--playback`;
                }
            } else {
                document.body.className = document.body.className.replace(' watch--playback', '');
            }
        }

        // media queries dirty the dom in the subnav, listen for them and clean it up
        if (this.props.mq && (this.props.mq !== nextProps.mq) && this.providerContainer) {
            // unmount the branding component entirely followed by mounting it again by 'nav.ready'
            ReactDOM.unmountComponentAtNode(this.providerContainer);
            if (this.subNavEl) {
                this.subNavEl.removeChild(this.providerContainer);
                this.providerContainer = null;
            }
        }
    }

    componentWillUnmount() {
        if (this.unlisten) {
            this.unlisten();
        }
        if (this.unsubscribeFromStore) {
            this.unsubscribeFromStore();
        }
    }

    mountBrandingComponent() {
        const { store } = this.context;
        this.insertProviderContainer();
        if (this.providerContainer) {
            ReactDOM.render(
                <Provider store={store}>
                    <IntlProvider>
                        <BrandingContainer />
                    </IntlProvider>
                </Provider>,
                this.providerContainer
            );
        }
    }

    // This is a hack to insert DOM into the nav element to hold the provider logo.  It will go away
    insertProviderContainer() {
        if (this.subNavEl === null) {
            this.subNavEl = document.querySelector('#global-nav-secondary .global-nav-container');
        }
        if (!this.subNavEl) {
            return;
        }

        if (!document.querySelector('#provider-logo-subnav')) {
            const providerLogo = document.createElement('div');
            providerLogo.id = 'provider-logo-subnav';
            this.providerContainer = this.subNavEl.appendChild(providerLogo);
        }
    }

    render() {
        return (
            <div>
                <Helmet
                    link={[
                        { class: 'page-type-include', rel: 'stylesheet', href: `${STYLE_HREF}/index.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch.css` },
                        { rel: 'stylesheet', href: `${STYLE_HREF}/watch-provider.css` },
                        {
                            class: '__watch-player-bundle__',
                            rel: 'stylesheet',
                            href: `${cdnHost}/players/web-player-bundle/${playerVersion}/espn-web-player-bundle.css`
                        }
                    ]}
                    style={[{
                        cssText: `
                            audio::-internal-media-controls-cast-button, video::-internal-media-controls-cast-button {
                                display: none;
                            }
                        `
                    }]}
                    script={[{
                        innerHTML: nrCookie
                    }]}
                />
                <AuthenticationContainer />
                <MessagingContainer />
                {this.props.children}
            </div>
        );
    }
}

PageShell.propTypes = {
    dispatch: React.PropTypes.func.isRequired,
    children: React.PropTypes.node,
    location: React.PropTypes.object,
    mq: React.PropTypes.string,
    player: React.PropTypes.object,
    routing: React.PropTypes.object
};
PageShell.contextTypes = {
    store: React.PropTypes.object.isRequired,
    router: React.PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    messages: React.PropTypes.object.isRequired
};

PageShell.renderTemplate = 'pageShell';
PageShell.loadActions = [fetchExternalConfig, loadScoringData, fetchClient];
PageShell.onLoadStart = onLoadingStart;
PageShell.onComplete = onFetchComponentData;

const mapStateToProps = (state) => {
    const player = playerSelector(state);
    const routing = routingSelector(state);
    return {
        mq: appSelector(state).mq,
        player,
        routing
    };
};

export default connect(mapStateToProps)(connectAdInitializer(PageShell));



// WEBPACK FOOTER //
// ./app/subShell/PageShell.js